import { useCallback, useContext, useEffect, useMemo } from "react";
import { PinsContext } from "../components/common/PinsProvider";

const NOOP_SORT = (items) => items;

export const usePins = (namespace) => {
  const ctx = useContext(PinsContext);

  useEffect(() => {
    // No namespace means no pin UI yet — the always-mounted, closed CardSelectorModal.
    // Loading there would run before sign-in completes and latch pins as unavailable.
    if (!namespace) return;
    ctx?.ensureLoaded();
  }, [ctx, namespace]);

  const list = useMemo(() => ctx?.pins?.[namespace] ?? [], [ctx, namespace]);
  const pinnedSet = useMemo(() => new Set(list), [list]);

  const sortPinnedFirst = useCallback(
    (items, getId) => {
      if (!items?.length || !list.length) return items;
      const order = new Map(list.map((id, index) => [id, index]));
      const rank = (item) => order.get(getId(item)) ?? Number.MAX_SAFE_INTEGER;
      return [...items].sort((a, b) => rank(a) - rank(b));
    },
    [list],
  );

  const togglePin = useCallback(
    (id) => ctx?.toggle(namespace, id),
    [ctx, namespace],
  );

  if (!ctx) {
    return {
      isPinned: () => false,
      togglePin: () => {},
      sortPinnedFirst: NOOP_SORT,
      canPin: false,
    };
  }

  return {
    isPinned: (id) => pinnedSet.has(id),
    togglePin,
    sortPinnedFirst,
    canPin: ctx.status === "ready",
  };
};

export default usePins;
